/**
 * TeamSystem Impact - Create Token Dialog
 * Dialog per creare un nuovo token MCP. Mostra il token una sola volta.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { agentTokenApi, CreateTokenDTO } from '../api/agentToken.api';
import { useAuthStore } from '../../../store/authStore';

type AccessLabel = 'read' | 'write' | 'config';

interface ScopeOption {
  value: string;
  access: AccessLabel;
  adminOnly?: boolean;
  /** Visible only to full administrators (backend gates this scope on 'amministratore'). */
  amministratoreOnly?: boolean;
  /** Explicit label; when set, overrides the read/write translation. */
  label?: string;
}

interface ScopeGroup {
  label: string;
  scopes: ScopeOption[];
}

const SCOPE_GROUPS: ScopeGroup[] = [
  {
    label: 'ESG / DMA',
    scopes: [
      { value: 'dma:read', access: 'read' },
      { value: 'dma:write', access: 'write', adminOnly: true },
    ],
  },
  {
    label: 'Project Management',
    scopes: [
      { value: 'pm:read', access: 'read' },
      { value: 'pm:write', access: 'write', adminOnly: true },
    ],
  },
  {
    label: 'Data Capture',
    scopes: [
      { value: 'datacapture:read', access: 'read' },
      { value: 'datacapture:write', access: 'write', adminOnly: true },
      { value: 'datacapture:config', access: 'config', adminOnly: true, amministratoreOnly: true, label: 'Configurazione' },
    ],
  },
  {
    label: 'Engagement',
    scopes: [
      { value: 'engagement:read', access: 'read', adminOnly: true },
      { value: 'engagement:write', access: 'write' },
    ],
  },
];

interface CreateTokenDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreateTokenDialog({ open, onClose, onSuccess }: CreateTokenDialogProps) {
  const { t } = useTranslation();
  const utente = useAuthStore((s) => s.utente);
  const isAdmin = utente?.ruolo === 'amministratore' || utente?.ruolo === 'responsabile_esg';
  const isAmministratore = utente?.ruolo === 'amministratore';

  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['dma:read', 'pm:read', 'engagement:write']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const nameError = submitted && !name.trim();

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleClose = () => {
    const hadToken = !!createdToken;
    setName('');
    setSelectedScopes(['dma:read', 'pm:read', 'engagement:write']);
    setError(null);
    setSubmitted(false);
    setCreatedToken(null);
    setCopied(false);
    onClose();
    if (hadToken) onSuccess?.();
  };

  const handleCreate = async () => {
    setSubmitted(true);
    setError(null);
    if (!name.trim()) return;

    setSaving(true);
    try {
      const dto: CreateTokenDTO = {
        agentName: name.trim(),
        scopes: selectedScopes.length > 0 ? selectedScopes : undefined,
      };
      const result = await agentTokenApi.create(dto);
      setCreatedToken(result.agentToken);
    } catch (err: any) {
      setError(err.message || t('settings.tokens.createError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    await navigator.clipboard.writeText(createdToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('settings.tokens.newTokenApi')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {createdToken ? (
            <>
              <Alert severity="warning">
                {t('settings.tokens.copyWarning')}
              </Alert>
              <TextField
                label={t('settings.tokens.tokenLabel')}
                value={createdToken}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopy} edge="end" aria-label={t('settings.tokens.copyAria')}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="success.main" aria-live="polite">
                {copied ? t('settings.tokens.copied') : ''}
              </Typography>
            </>
          ) : (
            <>
              <TextField
                label={t('settings.tokens.agentName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                fullWidth
                required
                error={nameError}
                helperText={nameError ? t('settings.tokens.requiredField') : ''}
                placeholder={t('settings.tokens.agentNamePlaceholder')}
              />
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {t('settings.tokens.permissions')}
              </Typography>
              {SCOPE_GROUPS.map((group) => {
                const visibleScopes = group.scopes.filter(
                  (scope) =>
                    (!scope.adminOnly || isAdmin) &&
                    (!scope.amministratoreOnly || isAmministratore)
                );
                if (visibleScopes.length === 0) return null;
                return (
                  <Box key={group.label}>
                    <Typography variant="caption" color="text.secondary">
                      {group.label}
                    </Typography>
                    <FormGroup row>
                      {visibleScopes.map((scope) => (
                        <FormControlLabel
                          key={scope.value}
                          control={
                            <Checkbox
                              checked={selectedScopes.includes(scope.value)}
                              onChange={() => toggleScope(scope.value)}
                              size="small"
                            />
                          }
                          label={scope.label ?? t(`settings.tokens.${scope.access}`)}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {createdToken ? (
          <Button onClick={handleClose} variant="contained">
            {t('settings.tokens.close')}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={saving}>
              {t('settings.tokens.cancel')}
            </Button>
            <Button onClick={handleCreate} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : t('settings.tokens.createToken')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
